import { Component, Inject, OnInit } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { Store } from '@ngrx/store'
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService, MsalBroadcastService } from '@azure/msal-angular'
import { EventMessage, InteractionStatus, PopupRequest, AuthenticationResult, EventType } from '@azure/msal-browser'
import { Subject, filter, takeUntil } from 'rxjs'
import { AppState } from 'src/store/state/app.state'
import {start_sso_login, start_support_user_login} from 'src/store/action/auth.action'
import {ActivatedRoute} from "@angular/router";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  isIframe = false
  loginDisplay = false
  private readonly _destroying$ = new Subject<void>()

  constructor(
    private appState: Store<AppState>,
    private route: ActivatedRoute,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,

  ) {}

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get('token');
    if (token) {
      this.appState.dispatch(start_support_user_login({token}))
    } else {
      this.initMSA()
    }
  }

  private async initMSA() {
    await this.authService.instance.initialize()

    this.isIframe = window !== window.parent && !window.opener // Remove this line to use Angular Universal
    this.setLoginDisplay()

    this.authService.instance.enableAccountStorageEvents() // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/'
        } else {
          this.setLoginDisplay()
        }
      })

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay()
        this.checkAndSetActiveAccount()
      })
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount()

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts()
      this.authService.instance.setActiveAccount(accounts[0])
    }
  }

  ssoLoginPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.authService
        .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account)
          this.sendTokenToBE(response.idToken)
        })
    } else {
      this.authService.loginPopup().subscribe((response: AuthenticationResult) => {
        this.authService.instance.setActiveAccount(response.account)
        this.sendTokenToBE(response.idToken)
      })
    }
  }

  // Sends the ID token (issued for this app): the backend verifies its signature, tenant and audience.
  sendTokenToBE(access_token: string) {
    this.appState.dispatch(
      start_sso_login({
        access_token
      })
    )
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined)
    this._destroying$.complete()
  }
}
